import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock3, DoorOpen, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";

const emptyBooking = {
  amenity_id: "",
  unit_id: "",
  date: "",
  start_time: "",
  end_time: "",
  purpose: "",
  participants: "",
  accept_terms: false,
};
const EMPTY_STATE_CLASS = [
  "border border-dashed border-slate-300 bg-white p-5",
  "text-sm text-slate-600",
].join(" ");
const AMENITY_CARD_CLASS = [
  "border border-slate-200 bg-white p-5 transition-colors",
  "hover:border-blue-500",
].join(" ");

function Status({ value }) {
  const colors = {
    CONFIRMED: "bg-emerald-100 text-emerald-800",
    PENDING_APPROVAL: "bg-amber-100 text-amber-800",
    REJECTED: "bg-rose-100 text-rose-800",
    CANCELLED: "bg-slate-100 text-slate-700",
    COMPLETED: "bg-blue-100 text-blue-800",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
        colors[value] || "bg-slate-100 text-slate-700"
      }`}
      data-testid={`resident-amenity-status-${String(value).toLowerCase()}`}
    >
      {String(value || "UNKNOWN").replaceAll("_", " ")}
    </span>
  );
}

function localDateTime(date, clock) {
  return date && clock ? `${date}T${clock}` : "";
}

export default function ResidentAmenities() {
  const [amenities, setAmenities] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [context, setContext] = useState({
    units: [],
    timezone: "Asia/Kolkata",
  });
  const [booking, setBooking] = useState(emptyBooking);
  const [availability, setAvailability] = useState(null);
  const [tab, setTab] = useState("discover");

  const selectedAmenity = useMemo(
    () => amenities?.find((item) => item.amenity_id === booking.amenity_id),
    [amenities, booking.amenity_id],
  );

  const load = async () => {
    try {
      const [amenityResponse, bookingResponse, contextResponse] =
        await Promise.all([
          api.get("/resident-amenities/amenities"),
          api.get("/resident-amenities/bookings"),
          api.get("/resident-amenities/context"),
        ]);
      setAmenities(amenityResponse.data);
      setBookings(bookingResponse.data);
      setContext(contextResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load amenities");
      setAmenities([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const loadAvailability = async () => {
      if (!booking.amenity_id || !booking.date) {
        setAvailability(null);
        return;
      }
      try {
        const response = await api.get(
          `/resident-amenities/amenities/${booking.amenity_id}/availability`,
          { params: { date_value: booking.date } },
        );
        setAvailability(response.data);
      } catch (error) {
        setAvailability(null);
        toast.error(
          error.response?.data?.detail || "Could not load availability",
        );
      }
    };
    loadAvailability();
  }, [booking.amenity_id, booking.date]);

  const chooseAmenity = (amenityId) => {
    setBooking({ ...emptyBooking, amenity_id: amenityId, date: booking.date });
    setTab("book");
  };

  const chooseSlot = (slot) => {
    setBooking({
      ...booking,
      start_time: slot.start_local.slice(11, 16),
      end_time: slot.end_local.slice(11, 16),
    });
  };

  const submit = async () => {
    try {
      await api.post("/resident-amenities/bookings", {
        ...booking,
        participants: Number(booking.participants || 0),
        start_local: localDateTime(booking.date, booking.start_time),
        end_local: localDateTime(booking.date, booking.end_time),
      });
      toast.success(
        selectedAmenity?.approval_required
          ? "Booking sent for approval"
          : "Booking confirmed",
      );
      setBooking(emptyBooking);
      setAvailability(null);
      setTab("bookings");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create booking");
    }
  };

  const cancel = async (bookingId) => {
    const reason = window.prompt("Cancellation reason (optional)");
    if (reason === null) return;
    try {
      await api.post(`/resident-amenities/bookings/${bookingId}/cancel`, {
        reason,
      });
      toast.success("Booking cancelled");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not cancel booking");
    }
  };

  if (!amenities) return <Loading />;
  return (
    <div data-testid="resident-amenities-page">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Shared spaces
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          Amenities & Bookings
        </h1>
        <p
          className="mt-2 text-sm text-slate-600"
          data-testid="resident-amenity-timezone"
        >
          Availability is shown in Society time: {context.timezone}
        </p>
      </header>

      <div
        className="mt-6 flex flex-wrap gap-2"
        data-testid="resident-amenity-tabs"
      >
        {[
          ["discover", "Amenities"],
          ["book", "Book a facility"],
          ["bookings", "My bookings"],
        ].map(([value, label]) => (
          <Button
            data-testid={`resident-amenity-tab-${value}`}
            key={value}
            onClick={() => setTab(value)}
            size="sm"
            variant={tab === value ? "default" : "outline"}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === "discover" && (
        <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {amenities.length === 0 ? (
            <p className={EMPTY_STATE_CLASS}>
              No resident-bookable amenities are available yet.
            </p>
          ) : (
            amenities.map((amenity) => (
              <article
                className={AMENITY_CARD_CLASS}
                data-testid={`resident-amenity-card-${amenity.amenity_id}`}
                key={amenity.amenity_id}
              >
                <DoorOpen className="h-5 w-5 text-blue-700" />
                <h2 className="mt-4 font-display text-xl font-semibold">
                  {amenity.name}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {amenity.location || amenity.category || "Society facility"}
                </p>
                <p className="mt-3 text-sm text-slate-600">
                  {amenity.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span>Capacity {amenity.capacity || "—"}</span>
                  <span>
                    {amenity.approval_required
                      ? "Approval required"
                      : "Instant confirmation"}
                  </span>
                </div>
                <Button
                  className="mt-5"
                  data-testid={`resident-amenity-book-${amenity.amenity_id}`}
                  onClick={() => chooseAmenity(amenity.amenity_id)}
                  size="sm"
                >
                  <Plus className="h-4 w-4" />
                  Book
                </Button>
              </article>
            ))
          )}
        </section>
      )}

      {tab === "book" && (
        <section
          className="mt-6 max-w-4xl border border-slate-200 bg-white p-5"
          data-testid="resident-amenity-booking-form"
        >
          <h2 className="font-display text-xl font-semibold">
            Reserve a facility
          </h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium">
              Amenity
              <select
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2"
                data-testid="resident-amenity-select"
                onChange={(event) =>
                  setBooking({ ...booking, amenity_id: event.target.value })
                }
                value={booking.amenity_id}
              >
                <option value="">Select an amenity</option>
                {amenities.map((amenity) => (
                  <option key={amenity.amenity_id} value={amenity.amenity_id}>
                    {amenity.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              Authorized unit
              <select
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2"
                data-testid="resident-amenity-unit-select"
                onChange={(event) =>
                  setBooking({ ...booking, unit_id: event.target.value })
                }
                value={booking.unit_id}
              >
                <option value="">Select your unit</option>
                {context.units.map((unit) => (
                  <option key={unit.unit_id} value={unit.unit_id}>
                    {unit.unit_number}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              Date
              <Input
                className="mt-1"
                data-testid="resident-amenity-date-input"
                onChange={(event) =>
                  setBooking({ ...booking, date: event.target.value })
                }
                type="date"
                value={booking.date}
              />
            </label>
            <label className="text-sm font-medium">
              Participants
              <Input
                className="mt-1"
                data-testid="resident-amenity-participants-input"
                min="0"
                onChange={(event) =>
                  setBooking({ ...booking, participants: event.target.value })
                }
                type="number"
                value={booking.participants}
              />
            </label>
            <label className="text-sm font-medium">
              Start time
              <Input
                className="mt-1"
                data-testid="resident-amenity-start-time-input"
                onChange={(event) =>
                  setBooking({ ...booking, start_time: event.target.value })
                }
                type="time"
                value={booking.start_time}
              />
            </label>
            <label className="text-sm font-medium">
              End time
              <Input
                className="mt-1"
                data-testid="resident-amenity-end-time-input"
                onChange={(event) =>
                  setBooking({ ...booking, end_time: event.target.value })
                }
                type="time"
                value={booking.end_time}
              />
            </label>
          </div>
          <label className="mt-4 block text-sm font-medium">
            Purpose
            <Textarea
              className="mt-1"
              data-testid="resident-amenity-purpose-input"
              onChange={(event) =>
                setBooking({ ...booking, purpose: event.target.value })
              }
              value={booking.purpose}
            />
          </label>
          {selectedAmenity?.terms && (
            <p
              className="mt-4 border-l-2 border-blue-500 bg-blue-50 p-3 text-sm text-blue-900"
              data-testid="resident-amenity-terms"
            >
              {selectedAmenity.terms}
            </p>
          )}
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input
              checked={booking.accept_terms}
              data-testid="resident-amenity-accept-terms"
              onChange={(event) =>
                setBooking({ ...booking, accept_terms: event.target.checked })
              }
              type="checkbox"
            />
            I accept the Society rules and terms.
          </label>
          {availability && (
            <div className="mt-6" data-testid="resident-amenity-availability">
              <h3 className="font-display font-semibold">
                Availability for {availability.date}
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {availability.slots.map((slot) => (
                  <Button
                    data-testid={`resident-amenity-slot-${slot.start_local}`}
                    disabled={slot.state !== "AVAILABLE"}
                    key={`${slot.start_local}-${slot.end_local}`}
                    onClick={() => chooseSlot(slot)}
                    size="sm"
                    variant={
                      slot.state === "AVAILABLE" ? "outline" : "secondary"
                    }
                  >
                    <Clock3 className="h-3.5 w-3.5" />
                    {slot.start_local.slice(11, 16)}–
                    {slot.end_local.slice(11, 16)} · {slot.state}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <Button
            className="mt-6"
            data-testid="resident-amenity-submit"
            onClick={submit}
          >
            <CalendarDays className="h-4 w-4" />
            {selectedAmenity?.approval_required
              ? "Submit for approval"
              : "Confirm booking"}
          </Button>
        </section>
      )}

      {tab === "bookings" && (
        <section
          className="mt-6 space-y-3"
          data-testid="resident-amenity-booking-list"
        >
          {bookings.length === 0 ? (
            <p className={EMPTY_STATE_CLASS}>
              You have not made any amenity bookings.
            </p>
          ) : (
            bookings.map((item) => (
              <article
                className="border border-slate-200 bg-white p-4"
                data-testid={`resident-booking-${item.booking_id}`}
                key={item.booking_id}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-lg font-semibold">
                      {item.amenity_snapshot?.name}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {item.booking_number} · Unit{" "}
                      {item.unit_snapshot?.unit_number}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {item.start_local?.replace("T", " ")} to{" "}
                      {item.end_local?.slice(11, 16)}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                      {item.purpose}
                    </p>
                    {item.rejection?.reason && (
                      <p className="mt-2 text-sm text-rose-700">
                        {item.rejection.reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Status value={item.status} />
                    {["CONFIRMED", "PENDING_APPROVAL"].includes(
                      item.status,
                    ) && (
                      <Button
                        data-testid={`resident-booking-cancel-${item.booking_id}`}
                        onClick={() => cancel(item.booking_id)}
                        size="sm"
                        variant="outline"
                      >
                        <XCircle className="h-4 w-4" /> Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </article>
            ))
          )}
        </section>
      )}
    </div>
  );
}
